import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    User,
    ShoppingCart,
    CreditCard,
    FileCheck,
    ChevronLeft,
    ChevronRight,
    Check,
    Save,
    Loader2,
} from 'lucide-react';
import { Customer } from '../services/clientsService';
import { parseMoney } from '../utils/format';
import Step1ClientSelection from '../components/SalesComponents/Step1ClientSelection';
import Step2SaleDetails from '../components/SalesComponents/Step2SaleDetails';
import Step3PaymentConditions from '../components/SalesComponents/Step3PaymentConditions';
import Step4Summary from '../components/SalesComponents/Step4Summary';
import ClientSelectorModal from '../components/SalesComponents/ClientSelectorModal';
import { useNavigate } from 'react-router-dom';
import { PaymentMethod, CreateSaleRequest, salesService } from '../services/saleService';
import { showSuccess, showError, showWarning } from '../components/Alerts';

interface Step {
    id: number;
    title: string;
    shortTitle: string;
    description: string;
    icon: React.ReactNode;
}

const steps: Step[] = [
    {
        id: 1,
        title: 'Selección de Cliente',
        shortTitle: 'Cliente',
        description: 'Elija el cliente para la venta',
        icon: <User className="w-4 h-4" />,
    },
    {
        id: 2,
        title: 'Detalle de Venta',
        shortTitle: 'Detalle',
        description: 'Unidades y condiciones',
        icon: <ShoppingCart className="w-4 h-4" />,
    },
    {
        id: 3,
        title: 'Condiciones de Pago',
        shortTitle: 'Pagos',
        description: 'Métodos de pago',
        icon: <CreditCard className="w-4 h-4" />,
    },
    {
        id: 4,
        title: 'Revisión Final',
        shortTitle: 'Resumen',
        description: 'Verifique y confirme',
        icon: <FileCheck className="w-4 h-4" />,
    },
];

interface SaleData {
    clientId: number | null;
    functionalUnits: Array<{ functionalUnitId: number; priceAtSale: number }>;
    saleDate: string;
    currency: 'ARS' | 'USD';
    exchangeRate: string;
    comments: string;
    total: number;
    paymentMethods: PaymentMethod[];
}

export default function Sales() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedClient, setSelectedClient] = useState<Customer | null>(null);
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [selectedUnits, setSelectedUnits] = useState<any[]>([]);
    const [closedPrices, setClosedPrices] = useState<{ [key: number]: string }>({});

    const [saleData, setSaleData] = useState<SaleData>({
        clientId: null,
        functionalUnits: [],
        saleDate: new Date().toISOString().split('T')[0],
        currency: 'USD',
        exchangeRate: '',
        comments: '',
        total: 0,
        paymentMethods: [],
    });

    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

    const handleNext = () => {
        if (currentStep < steps.length) setCurrentStep(currentStep + 1);
    };

    const handlePrevious = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const isStep1Valid = (): boolean => saleData.clientId !== null;

    const isStep2Valid = (): boolean => {
        if (!saleData.saleDate) return false;
        if (saleData.currency !== 'ARS' && saleData.currency !== 'USD') return false;
        if (selectedUnits.length === 0) return false;
        if (saleData.currency === 'ARS') {
            const rate = parseFloat(saleData.exchangeRate);
            if (!saleData.exchangeRate || isNaN(rate) || rate <= 0) return false;
        }
        return true;
    };

    const isStep3Valid = (): boolean => {
        const totalPaid = paymentMethods.reduce((sum, method) => {
            const rawAmount = parseMoney(method.amount) || 0;
            const rate = parseFloat(method.exchangeRate) || 1;
            let amountInBaseCurrency = rawAmount;
            if (method.currency !== saleData.currency) {
                if (saleData.currency === 'USD') {
                    amountInBaseCurrency = rawAmount / rate;
                } else {
                    amountInBaseCurrency = rawAmount * rate;
                }
            }
            return sum + amountInBaseCurrency;
        }, 0);
        return Math.abs(totalPaid - saleData.total) < 0.01;
    };

    const isStepValid = (stepId: number): boolean => {
        switch (stepId) {
            case 1: return isStep1Valid();
            case 2: return isStep2Valid();
            case 3: return isStep3Valid();
            case 4: return true;
            default: return false;
        }
    };

    const canProceedToNextStep = (): boolean => isStepValid(currentStep);

    const handleSelectClient = () => setIsClientModalOpen(true);

    const handleClientSelected = (client: Customer) => {
        setSelectedClient(client);
        setSaleData((prev) => ({ ...prev, clientId: client.id }));
    };

    useEffect(() => {
        const exchangeRateNum = parseFloat(saleData.exchangeRate) || 0;
        const total = selectedUnits.reduce((sum, unit) => {
            const listInCurrency =
                saleData.currency === 'ARS' ? unit.listPrice * exchangeRateNum : unit.listPrice;
            const closedPriceValue = closedPrices[unit.id] || '';
            const closedPriceNum = parseMoney(closedPriceValue);
            const final = closedPriceNum || listInCurrency;
            return sum + final;
        }, 0);

        setSaleData((prev) => ({
            ...prev,
            functionalUnits: selectedUnits.map((unit) => {
                const listInCurrency =
                    saleData.currency === 'ARS' ? unit.listPrice * exchangeRateNum : unit.listPrice;
                const closedPriceValue = closedPrices[unit.id] || '';
                const closedPriceNum = parseMoney(closedPriceValue);
                return {
                    functionalUnitId: unit.id,
                    priceAtSale: closedPriceNum || listInCurrency,
                };
            }),
            total: total,
        }));
    }, [selectedUnits, saleData.currency, saleData.exchangeRate, closedPrices]);

    const buildSaleJSON = (): CreateSaleRequest => {
        const mapFinancingType = (type?: string): string | undefined => {
            if (!type) return undefined;
            return type.toUpperCase().replace(/ /g, '_');
        };

        return {
            clientId: saleData.clientId!,
            functionalUnits: selectedUnits.map((unit) => {
                const exchangeRateNum = parseFloat(saleData.exchangeRate) || 0;
                const listInCurrency =
                    saleData.currency === 'ARS'
                        ? unit.listPrice * exchangeRateNum
                        : unit.listPrice;
                const closedPriceValue = closedPrices[unit.id] || '';
                const closedPriceNum = parseMoney(closedPriceValue);
                return {
                    functionalUnitId: unit.id,
                    priceAtSale: closedPriceNum || listInCurrency,
                };
            }),
            deliveryDate: saleData.saleDate,
            observations: saleData.comments || undefined,
            currency: saleData.currency,
            exchangeRate: parseFloat(saleData.exchangeRate) || 1,
            total: saleData.total,
            paymentMethods: paymentMethods.map((method) => ({
                currency: method.currency,
                paymentType: method.paymentType === 'cash' ? 'EFECTIVO' : 'FINANCIADO',
                accountingType: method.accountingType,
                description: method.description || '',
                amount: parseMoney(method.amount) || 0,
                exchangeRate: parseFloat(method.exchangeRate) || 1,
                ...(method.paymentType === 'cash' && {
                    paymentDate: method.paymentDate,
                    iva: parseFloat(method.iva) || undefined,
                }),
                ...(method.paymentType === 'financing' && {
                    financingType: mapFinancingType(method.financingType),
                    firstInstallmentDate: method.firstInstallmentDate,
                    installments: method.installments,
                    ...(method.currency === 'ARS' && method.updateIndex && {
                        indexationType: method.updateIndex,
                        indexBaseDate: method.indexBaseDate,
                    }),
                    iva: parseFloat(method.iva) || undefined,
                }),
                ...(parseFloat(method.moraRate || '') > 0 && !method.updateIndex && {
                    moraRate: parseFloat(method.moraRate!),
                    ...(method.moraFrequency && { moraFrequency: method.moraFrequency }),
                }),
            })),
        };
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const salePayload = buildSaleJSON();
            const sale = await salesService.createSale(salePayload);
            if (sale.warnings && sale.warnings.length > 0) {
                // Mostrar cada warning como toast separado antes de navegar
                sale.warnings.forEach((w: string) => showWarning(w));
                showSuccess('Venta creada con advertencias. Revisá los avisos.');
            } else {
                showSuccess('Venta creada exitosamente');
            }
            navigate('/sales-history');
        } catch (error: any) {
            console.error('Error creating sale:', error);
            const backendMessage = error?.response?.data?.message;
            showError(backendMessage || 'Error al crear la venta. Por favor intente nuevamente.');
            setIsLoading(false);
        }
    };

    const goToStep = (stepId: number) => {
        if (stepId < currentStep) setCurrentStep(stepId);
    };

    const getStepSummaryBadge = (stepId: number): string | null => {
        switch (stepId) {
            case 1:
                if (selectedClient) {
                    return selectedClient.type === 'PERSON'
                        ? `${(selectedClient as any).firstName} ${(selectedClient as any).lastName}`
                        : (selectedClient as any).businessName;
                }
                return null;
            case 2:
                if (selectedUnits.length > 0) return `${selectedUnits.length} UF · ${saleData.currency}`;
                return null;
            case 3:
                if (paymentMethods.length > 0) return `${paymentMethods.length} método${paymentMethods.length !== 1 ? 's' : ''}`;
                return null;
            default:
                return null;
        }
    };

    return (
        <div className="w-full h-full p-4 sm:p-6 lg:p-8 space-y-6">
            {/* ===== HEADER ===== */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Nueva Venta</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Complete los pasos para registrar una nueva venta
                    </p>
                </div>
                <Badge variant="outline" className="text-xs font-medium w-fit px-3 py-1">
                    Paso {currentStep} de {steps.length}
                </Badge>
            </div>

            {/* ===== STEPPER — Desktop ===== */}
            <div className="hidden md:block">
                <div className="bg-card border border-border rounded-xl p-5">
                    <div className="relative">
                        {/* Background track */}
                        <div className="absolute top-5 left-[12.5%] right-[12.5%] h-px bg-border" />
                        {/* Progress track */}
                        <div
                            className="absolute top-5 left-[12.5%] h-px bg-primary transition-all duration-500 ease-in-out"
                            style={{
                                width: `${((currentStep - 1) / (steps.length - 1)) * 75}%`,
                            }}
                        />

                        <div className="relative flex justify-between">
                            {steps.map((step) => {
                                const isActive = currentStep === step.id;
                                const isCompleted = currentStep > step.id;
                                const summary = getStepSummaryBadge(step.id);

                                return (
                                    <div
                                        key={step.id}
                                        className={`flex flex-col items-center w-1/4 ${isCompleted ? 'cursor-pointer' : ''}`}
                                        onClick={() => goToStep(step.id)}
                                    >
                                        {/* Circle */}
                                        <div
                                            className={`
                                                relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2
                                                ${isCompleted
                                                    ? 'bg-primary border-primary text-primary-foreground shadow-sm'
                                                    : isActive
                                                        ? 'bg-primary border-primary text-primary-foreground shadow-md shadow-primary/20 scale-110'
                                                        : 'bg-background border-border text-muted-foreground'
                                                }
                                            `}
                                        >
                                            {isCompleted ? (
                                                <Check className="w-4 h-4" />
                                            ) : (
                                                step.icon
                                            )}
                                        </div>

                                        {/* Labels */}
                                        <div className="mt-3 text-center">
                                            <p
                                                className={`text-xs font-semibold transition-colors ${
                                                    isActive
                                                        ? 'text-primary'
                                                        : isCompleted
                                                            ? 'text-foreground'
                                                            : 'text-muted-foreground'
                                                }`}
                                            >
                                                {step.shortTitle}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground mt-0.5 hidden lg:block">
                                                {step.description}
                                            </p>
                                            {isCompleted && summary && (
                                                <Badge
                                                    variant="secondary"
                                                    className="mt-1.5 text-[10px] font-normal px-2 py-0 max-w-[90%] truncate"
                                                    title={summary}
                                                >
                                                    {summary}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== STEPPER — Mobile ===== */}
            <div className="md:hidden">
                <div className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2">
                        {steps.map((step) => {
                            const isActive = currentStep === step.id;
                            const isCompleted = currentStep > step.id;
                            return (
                                <React.Fragment key={step.id}>
                                    <div
                                        className={`
                                            w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all text-xs font-bold border-2
                                            ${isCompleted
                                                ? 'bg-primary border-primary text-primary-foreground'
                                                : isActive
                                                    ? 'bg-primary border-primary text-primary-foreground ring-4 ring-primary/20'
                                                    : 'bg-background border-border text-muted-foreground'
                                            }
                                        `}
                                        onClick={() => goToStep(step.id)}
                                    >
                                        {isCompleted ? <Check className="w-3.5 h-3.5" /> : step.id}
                                    </div>
                                    {step.id < steps.length && (
                                        <div
                                            className={`flex-1 h-px transition-colors ${
                                                isCompleted ? 'bg-primary' : 'bg-border'
                                            }`}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <div className="mt-3">
                        <p className="text-sm font-semibold text-foreground">{steps[currentStep - 1].title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{steps[currentStep - 1].description}</p>
                    </div>
                </div>
            </div>

            {/* ===== STEP CONTENT ===== */}
            <div className="min-h-[400px]">
                {currentStep === 1 && (
                    <Step1ClientSelection
                        selectedClient={selectedClient}
                        onSelectClient={handleSelectClient}
                    />
                )}

                {currentStep === 2 && (
                    <Step2SaleDetails
                        saleDate={saleData.saleDate}
                        currency={saleData.currency}
                        exchangeRate={saleData.exchangeRate}
                        comments={saleData.comments}
                        selectedUnits={selectedUnits}
                        onSaleDateChange={(date) =>
                            setSaleData((prev) => ({ ...prev, saleDate: date }))
                        }
                        onCurrencyChange={(currency) => {
                            setClosedPrices({});
                            setSaleData((prev) => ({
                                ...prev,
                                currency,
                                exchangeRate: currency === 'ARS' ? '' : '',
                            }));
                        }}
                        onExchangeRateChange={(rate) =>
                            setSaleData((prev) => ({ ...prev, exchangeRate: rate }))
                        }
                        onCommentsChange={(comments) =>
                            setSaleData((prev) => ({ ...prev, comments }))
                        }
                        onSelectedUnitsChange={setSelectedUnits}
                        closedPrices={closedPrices}
                        onClosedPricesChange={setClosedPrices}
                        saleData={saleData}
                    />
                )}

                {currentStep === 3 && (
                    <Step3PaymentConditions
                        totalAmount={saleData.total}
                        baseCurrency={saleData.currency}
                        paymentMethods={paymentMethods}
                        onPaymentMethodsChange={setPaymentMethods}
                        exchangeRate={saleData.exchangeRate}
                        onExchangeRateChange={(rate) =>
                            setSaleData((prev) => ({ ...prev, exchangeRate: rate }))
                        }
                    />
                )}

                {currentStep === 4 && (
                    <Step4Summary
                        clientName={
                            selectedClient
                                ? selectedClient.type === 'PERSON'
                                    ? `${selectedClient.firstName} ${selectedClient.lastName}`
                                    : selectedClient.businessName
                                : 'No seleccionado'
                        }
                        functionalUnits={selectedUnits}
                        currency={saleData.currency}
                        exchangeRate={saleData.exchangeRate}
                        total={saleData.total}
                        saleDate={saleData.saleDate}
                        comments={saleData.comments}
                        paymentMethods={paymentMethods}
                        closedPrices={closedPrices}
                    />
                )}
            </div>

            {/* ===== NAVIGATION BAR ===== */}
            {/* Intentionally not `sticky`: on short steps (e.g. the empty-state
                unit picker) a sticky-to-viewport-bottom bar pins itself on top
                of in-flow content instead of after it, making buttons like
                "Agregar Unidades" invisible and unclickable underneath it. */}
            <div>
                <div className="bg-card/95 backdrop-blur-sm border border-border rounded-xl shadow-lg shadow-black/5 px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* Previous button */}
                        <Button
                            variant="outline"
                            onClick={handlePrevious}
                            disabled={currentStep === 1}
                            className="gap-2"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Anterior</span>
                        </Button>

                        {/* Step dots */}
                        <div className="flex items-center gap-1.5">
                            {steps.map((step) => (
                                <div
                                    key={step.id}
                                    className={`rounded-full transition-all duration-300 ${
                                        currentStep === step.id
                                            ? 'w-6 h-2 bg-primary'
                                            : currentStep > step.id
                                                ? 'w-2 h-2 bg-primary/50'
                                                : 'w-2 h-2 bg-border'
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Next / Save button */}
                        {currentStep === 4 ? (
                            <Button
                                onClick={handleSave}
                                disabled={isLoading}
                                className="gap-2"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span className="hidden sm:inline">
                                    {isLoading ? 'Guardando...' : 'Confirmar Venta'}
                                </span>
                                <span className="sm:hidden">
                                    {isLoading ? '...' : 'Confirmar'}
                                </span>
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                disabled={!canProceedToNextStep()}
                                className="gap-2"
                            >
                                <span className="hidden sm:inline">Siguiente</span>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Client Selection Modal */}
            <ClientSelectorModal
                open={isClientModalOpen}
                onOpenChange={setIsClientModalOpen}
                onSelectClient={handleClientSelected}
            />
        </div>
    );
}
